import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { useLoginMutation } from '../Features/auth/authApiSlice';
import { setCredentials } from '../Features/auth/authSlice';

import {
    LockOpenIcon,
    LoginIcon,
    ShieldCheckIcon,
    MailIcon,
    KeyIcon,
    TicketIcon,
    ChartBarIcon,
    UserGroupIcon,
    TruckIcon,
    CheckCircleIcon,
    EyeIcon,
    EyeOffIcon,
    ArrowLeftIcon,
} from '@heroicons/react/outline';
import Logo from './Logo';

function UserRegisterView(props) {

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [login, { isLoading }] = useLoginMutation();

    const [input, setInput] = useState({
        email: '',
        password: '',
        errorMassage: '',
    });

    const [errors, setErrors] = useState({
        email: '',
        password: '',
    });

    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setInput({
            ...input,
            [name]: value,
            errorMassage: '',
        });

        setErrors({
            ...errors,
            [name]: '',
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setErrors({
            email: '',
            password: '',
        });

        setInput({
            ...input,
            errorMassage: '',
        });

        try {

            const userData = await login(input).unwrap();

            const token = userData.token;
            const user = userData.user;
            const roles = userData.roles;

            dispatch(
                setCredentials({
                    user,
                    token,
                    roles,
                })
            );

            if (user.is_manager == 1) {
                navigate('/manager');

            } else if (user.is_client_data_analyst_manager == 1) {
                navigate('/data-analysis');

            } else if (user.is_client_accountant == 1) {
                navigate('/accounting');

            } else if (user.is_client_promotor == 1) {
                navigate('/sales');

            } else if (user.is_client == 1) {
                navigate('/client');

            } else {
                navigate('/user/users');
            }

        } catch (err) {

            if (err?.data?.errors) {

                setErrors({
                    email: err.data.errors.email || '',
                    password: err.data.errors.password || '',
                });

            } else {

                setInput({
                    ...input,
                    errorMassage:
                        err?.data?.error ||
                        'Unable to sign in. Please check your email and password.',
                });
            }
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col">

            {/* =========================
                TOP NAVIGATION
            ========================== */}
            <header className="h-20 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">

                <div className="max-w-7xl mx-auto h-full px-5 sm:px-8 lg:px-10 flex items-center justify-between">

                    <Link to="/" className="flex items-center">
                        <Logo />
                    </Link>

                    <Link
                        to="/"
                        className="
                            inline-flex items-center gap-2
                            text-sm font-medium
                            text-gray-600 dark:text-gray-300
                            hover:text-[#80b643]
                            transition
                        "
                    >
                        <ArrowLeftIcon className="w-4 h-4" />
                        Back to Home
                    </Link>

                </div>

            </header>


            {/* =========================
                MAIN CONTENT
            ========================== */}
            <main className="flex-1 flex items-center">

                <div className="w-full max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 py-10 lg:py-16">

                    <div
                        className="
                            grid
                            lg:grid-cols-2
                            bg-white
                            dark:bg-gray-900
                            border border-gray-200
                            dark:border-gray-800
                            rounded-2xl
                            shadow-sm
                            overflow-hidden
                        "
                    >

                        {/* =========================
                            LEFT INFORMATION PANEL
                        ========================== */}
                        <div className="
                            hidden
                            lg:flex
                            flex-col
                            justify-between
                            p-10
                            xl:p-14
                            border-r
                            border-gray-200
                            dark:border-gray-800
                            relative
                            overflow-hidden
                        ">

                            {/* Decorative elements */}
                            <div className="
                                absolute
                                -top-24
                                -left-24
                                w-64
                                h-64
                                rounded-full
                                bg-[#80b643]/5
                            " />

                            <div className="
                                absolute
                                -bottom-32
                                -right-32
                                w-80
                                h-80
                                rounded-full
                                bg-[#80b643]/5
                            " />

                            <div className="relative z-10">

                                {/* Small accent */}
                                <div className="flex items-center gap-2 mb-8">

                                    <div className="w-10 h-1 rounded-full bg-[#80b643]" />

                                    <span className="
                                        text-xs
                                        font-semibold
                                        uppercase
                                        tracking-wider
                                        text-[#80b643]
                                    ">
                                        Transport Management
                                    </span>

                                </div>


                                <h1 className="
                                    text-3xl
                                    xl:text-4xl
                                    font-bold
                                    tracking-tight
                                    text-gray-900
                                    dark:text-white
                                    leading-tight
                                ">
                                    Everything you need to
                                    <span className="text-[#80b643]">
                                        {' '}run your transport business.
                                    </span>
                                </h1>


                                <p className="
                                    mt-5
                                    text-base
                                    text-gray-500
                                    dark:text-gray-400
                                    leading-7
                                    max-w-lg
                                ">
                                    TellerPoint brings ticket sales, payments,
                                    employees, routes, vehicles and business
                                    reporting together in one powerful platform.
                                </p>


                                {/* Feature list */}
                                <div className="mt-10 space-y-5">

                                    <div className="flex items-start gap-4">

                                        <div className="
                                            w-10
                                            h-10
                                            flex
                                            items-center
                                            justify-center
                                            rounded-xl
                                            bg-[#80b643]/10
                                            dark:bg-[#80b643]/20
                                            flex-shrink-0
                                        ">
                                            <TicketIcon className="w-5 h-5 text-[#80b643]" />
                                        </div>

                                        <div>
                                            <h3 className="
                                                text-sm
                                                font-semibold
                                                text-gray-900
                                                dark:text-white
                                            ">
                                                Fast Ticket Sales
                                            </h3>

                                            <p className="
                                                mt-1
                                                text-sm
                                                text-gray-500
                                                dark:text-gray-400
                                            ">
                                                Sell tickets quickly and manage
                                                seats with ease.
                                            </p>
                                        </div>

                                    </div>


                                    <div className="flex items-start gap-4">

                                        <div className="
                                            w-10
                                            h-10
                                            flex
                                            items-center
                                            justify-center
                                            rounded-xl
                                            bg-[#80b643]/10
                                            dark:bg-[#80b643]/20
                                            flex-shrink-0
                                        ">
                                            <ChartBarIcon className="w-5 h-5 text-[#80b643]" />
                                        </div>

                                        <div>
                                            <h3 className="
                                                text-sm
                                                font-semibold
                                                text-gray-900
                                                dark:text-white
                                            ">
                                                Powerful Reports
                                            </h3>

                                            <p className="
                                                mt-1
                                                text-sm
                                                text-gray-500
                                                dark:text-gray-400
                                            ">
                                                Monitor sales, payments and
                                                business performance.
                                            </p>
                                        </div>

                                    </div>


                                    <div className="flex items-start gap-4">

                                        <div className="
                                            w-10
                                            h-10
                                            flex
                                            items-center
                                            justify-center
                                            rounded-xl
                                            bg-[#80b643]/10
                                            dark:bg-[#80b643]/20
                                            flex-shrink-0
                                        ">
                                            <UserGroupIcon className="w-5 h-5 text-[#80b643]" />
                                        </div>

                                        <div>
                                            <h3 className="
                                                text-sm
                                                font-semibold
                                                text-gray-900
                                                dark:text-white
                                            ">
                                                Employee Management
                                            </h3>

                                            <p className="
                                                mt-1
                                                text-sm
                                                text-gray-500
                                                dark:text-gray-400
                                            ">
                                                Control staff access, shifts
                                                and POS activity.
                                            </p>
                                        </div>

                                    </div>


                                    <div className="flex items-start gap-4">

                                        <div className="
                                            w-10
                                            h-10
                                            flex
                                            items-center
                                            justify-center
                                            rounded-xl
                                            bg-[#80b643]/10
                                            dark:bg-[#80b643]/20
                                            flex-shrink-0
                                        ">
                                            <TruckIcon className="w-5 h-5 text-[#80b643]" />
                                        </div>

                                        <div>
                                            <h3 className="
                                                text-sm
                                                font-semibold
                                                text-gray-900
                                                dark:text-white
                                            ">
                                                Transport Operations
                                            </h3>

                                            <p className="
                                                mt-1
                                                text-sm
                                                text-gray-500
                                                dark:text-gray-400
                                            ">
                                                Manage routes, vehicles and
                                                your daily operations.
                                            </p>
                                        </div>

                                    </div>

                                </div>

                            </div>


                            {/* Bottom trust section */}
                            <div className="
                                relative
                                z-10
                                mt-12
                                pt-6
                                border-t
                                border-gray-200
                                dark:border-gray-800
                            ">

                                <div className="flex items-center gap-3">

                                    <div className="
                                        w-9
                                        h-9
                                        rounded-lg
                                        bg-[#80b643]/10
                                        flex
                                        items-center
                                        justify-center
                                    ">
                                        <ShieldCheckIcon className="w-5 h-5 text-[#80b643]" />
                                    </div>

                                    <div>
                                        <p className="
                                            text-sm
                                            font-semibold
                                            text-gray-800
                                            dark:text-gray-200
                                        ">
                                            Secure business access
                                        </p>

                                        <p className="
                                            text-xs
                                            text-gray-500
                                            dark:text-gray-400
                                        ">
                                            Your account and business data are
                                            protected.
                                        </p>
                                    </div>

                                </div>

                            </div>

                        </div>


                        {/* =========================
                            LOGIN PANEL
                        ========================== */}
                        <div className="
                            flex
                            items-center
                            justify-center
                            p-6
                            sm:p-10
                            lg:p-14
                            xl:p-16
                        ">

                            <div className="w-full max-w-md">

                                {/* Mobile logo */}
                                <div className="lg:hidden mb-10 flex justify-center">
                                    <Logo />
                                </div>


                                {/* Login header */}
                                <div className="mb-8">

                                    <div className="
                                        w-12
                                        h-12
                                        rounded-xl
                                        bg-[#80b643]/10
                                        dark:bg-[#80b643]/20
                                        flex
                                        items-center
                                        justify-center
                                        mb-5
                                    ">
                                        <LockOpenIcon className="w-6 h-6 text-[#80b643]" />
                                    </div>

                                    <h2 className="
                                        text-2xl
                                        sm:text-3xl
                                        font-bold
                                        tracking-tight
                                        text-gray-900
                                        dark:text-white
                                    ">
                                        Welcome
                                    </h2>

                                    <p className="
                                        mt-2
                                        text-sm
                                        text-gray-500
                                        dark:text-gray-400
                                    ">
                                        Sign in to access your TellerPoint
                                        account.
                                    </p>

                                </div>


                                {/* General error */}
                                {input.errorMassage && (
                                    <div className="
                                        mb-6
                                        rounded-xl
                                        border
                                        border-red-200
                                        dark:border-red-900/50
                                        bg-red-50
                                        dark:bg-red-900/10
                                        px-4
                                        py-3
                                    ">
                                        <p className="
                                            text-sm
                                            text-red-600
                                            dark:text-red-400
                                        ">
                                            {input.errorMassage}
                                        </p>
                                    </div>
                                )}


                                <form onSubmit={handleSubmit} className="space-y-5">

                                    {/* Email */}
                                    <div>

                                        <label
                                            htmlFor="email"
                                            className="
                                                block
                                                mb-2
                                                text-sm
                                                font-medium
                                                text-gray-700
                                                dark:text-gray-300
                                            "
                                        >
                                            Email address
                                        </label>

                                        <div className="relative">

                                            <MailIcon className="
                                                absolute
                                                left-3.5
                                                top-1/2
                                                -translate-y-1/2
                                                w-5
                                                h-5
                                                text-gray-400
                                            " />

                                            <input
                                                id="email"
                                                type="email"
                                                name="email"
                                                value={input.email}
                                                onChange={handleChange}
                                                autoComplete="email"
                                                placeholder="Enter your email"
                                                className={`
                                                    w-full
                                                    h-12
                                                    pl-11
                                                    pr-4
                                                    rounded-xl
                                                    border
                                                    ${
                                                        errors.email
                                                            ? 'border-red-400 focus:ring-red-100'
                                                            : 'border-gray-200 dark:border-gray-700 focus:border-[#80b643] focus:ring-[#80b643]/20'
                                                    }
                                                    bg-white
                                                    dark:bg-gray-950
                                                    text-gray-900
                                                    dark:text-white
                                                    placeholder-gray-400
                                                    outline-none
                                                    focus:ring-4
                                                    transition
                                                `}
                                            />

                                        </div>

                                        {errors.email && (
                                            <p className="
                                                mt-1.5
                                                text-xs
                                                text-red-500
                                            ">
                                                {errors.email}
                                            </p>
                                        )}

                                    </div>


                                    {/* Password */}
                                    <div>

                                        <div className="flex items-center justify-between mb-2">

                                            <label
                                                htmlFor="password"
                                                className="
                                                    text-sm
                                                    font-medium
                                                    text-gray-700
                                                    dark:text-gray-300
                                                "
                                            >
                                                Password
                                            </label>

                                        </div>

                                        <div className="relative">

                                            <KeyIcon className="
                                                absolute
                                                left-3.5
                                                top-1/2
                                                -translate-y-1/2
                                                w-5
                                                h-5
                                                text-gray-400
                                            " />

                                            <input
                                                id="password"
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                value={input.password}
                                                onChange={handleChange}
                                                autoComplete="current-password"
                                                placeholder="Enter your password"
                                                className={`
                                                    w-full
                                                    h-12
                                                    pl-11
                                                    pr-12
                                                    rounded-xl
                                                    border
                                                    ${
                                                        errors.password
                                                            ? 'border-red-400 focus:ring-red-100'
                                                            : 'border-gray-200 dark:border-gray-700 focus:border-[#80b643] focus:ring-[#80b643]/20'
                                                    }
                                                    bg-white
                                                    dark:bg-gray-950
                                                    text-gray-900
                                                    dark:text-white
                                                    placeholder-gray-400
                                                    outline-none
                                                    focus:ring-4
                                                    transition
                                                `}
                                            />

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setShowPassword(!showPassword)
                                                }
                                                className="
                                                    absolute
                                                    right-3
                                                    top-1/2
                                                    -translate-y-1/2
                                                    p-1.5
                                                    rounded-lg
                                                    text-gray-400
                                                    hover:text-[#80b643]
                                                    transition
                                                "
                                                aria-label={
                                                    showPassword
                                                        ? 'Hide password'
                                                        : 'Show password'
                                                }
                                            >
                                                {showPassword ? (
                                                    <EyeOffIcon className="w-5 h-5" />
                                                ) : (
                                                    <EyeIcon className="w-5 h-5" />
                                                )}
                                            </button>

                                        </div>

                                        {errors.password && (
                                            <p className="
                                                mt-1.5
                                                text-xs
                                                text-red-500
                                            ">
                                                {errors.password}
                                            </p>
                                        )}

                                    </div>


                                    {/* Login button */}
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="
                                            w-full
                                            h-12
                                            mt-2
                                            rounded-xl
                                            bg-[#80b643]
                                            hover:bg-[#719f3b]
                                            disabled:opacity-60
                                            disabled:cursor-not-allowed
                                            text-white
                                            font-semibold
                                            text-sm
                                            flex
                                            items-center
                                            justify-center
                                            gap-2
                                            shadow-sm
                                            hover:shadow-md
                                            transition
                                        "
                                    >

                                        {isLoading ? (
                                            <>
                                                <svg
                                                    className="animate-spin w-5 h-5"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    />

                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                    />
                                                </svg>

                                                Signing in...
                                            </>
                                        ) : (
                                            <>
                                                <LoginIcon className="w-5 h-5" />
                                                Sign In
                                            </>
                                        )}

                                    </button>

                                </form>


                                {/* Register */}
                                <div className="
                                    mt-8
                                    pt-6
                                    border-t
                                    border-gray-200
                                    dark:border-gray-800
                                    text-center
                                ">

                                    <p className="
                                        text-sm
                                        text-gray-500
                                        dark:text-gray-400
                                    ">
                                        Don't have an account?
                                        {' '}

                                        <Link
                                            to="/user/register"
                                            className="
                                                font-semibold
                                                text-[#80b643]
                                                hover:text-[#719f3b]
                                                transition
                                            "
                                        >
                                            Register your business
                                        </Link>
                                    </p>

                                </div>


                                {/* Security notice */}
                                <div className="
                                    mt-6
                                    flex
                                    items-start
                                    gap-3
                                    rounded-xl
                                    bg-gray-50
                                    dark:bg-gray-950
                                    border
                                    border-gray-100
                                    dark:border-gray-800
                                    p-4
                                ">

                                    <ShieldCheckIcon className="
                                        w-5
                                        h-5
                                        flex-shrink-0
                                        text-[#80b643]
                                    " />

                                    <p className="
                                        text-xs
                                        leading-5
                                        text-gray-500
                                        dark:text-gray-400
                                    ">
                                        Your login information is securely
                                        processed to protect your account
                                        and business data.
                                    </p>

                                </div>

                            </div>

                        </div>

                    </div>

                </div>

            </main>


            {/* =========================
                FOOTER
            ========================== */}
            <footer className="
                py-5
                text-center
                text-xs
                text-gray-400
                dark:text-gray-600
            ">
                © {new Date().getFullYear()} TellerPoint. All rights reserved.
            </footer>

        </div>
    );
}

export default UserRegisterView;